import { TOPIC_GRAPH_COMMENT_TYPE, TopicGraphNodeData } from './types';

/** Matches Ariel editor node width (CustomNode.tsx). */
const NODE_WIDTH = 250;
/** Matches rete-auto-arrange-plugin classic preset spacing. */
const DEFAULT_LAYOUT_SPACING = 100;
const DEFAULT_VERTICAL_PADDING = 50;

export type LayoutTopicGraphOptions = {
  /** Extra horizontal gap between node edges (default: 100). */
  layoutSpacing?: number;
  /** Extra vertical gap between stacked nodes (default: 50). */
  verticalPadding?: number;
};

const resolveLayoutOptions = (options?: LayoutTopicGraphOptions) => ({
  layoutSpacing: options?.layoutSpacing ?? DEFAULT_LAYOUT_SPACING,
  verticalPadding: options?.verticalPadding ?? DEFAULT_VERTICAL_PADDING,
});

const estimateNodeHeight = (node: TopicGraphNodeData): number => {
  const base = 110;

  if (node.type === 'SELECT' || node.type === 'MULTISELECT' || node.type === 'PERSON_MULTISELECT') {
    return base + Object.keys(node.choices).length * 34;
  }

  if (node.type === 'SCALE') {
    return base + (node.statements?.length ?? 0) * 28;
  }

  return base + 36;
};

const getQuestionNodes = (nodes: TopicGraphNodeData[]): TopicGraphNodeData[] =>
  nodes.filter((node) => node.type !== TOPIC_GRAPH_COMMENT_TYPE);

const computeLayers = (startIndex: number, nodeByIndex: Map<number, TopicGraphNodeData>): Map<number, number> => {
  const layers = new Map<number, number>();

  const visit = (index: number, depth: number) => {
    const previousDepth = layers.get(index);
    if (previousDepth !== undefined && previousDepth >= depth) {
      return;
    }

    layers.set(index, Math.max(previousDepth ?? 0, depth));

    const node = nodeByIndex.get(index);
    if (!node) {
      return;
    }

    Object.keys(node.outputs)
      .sort()
      .forEach((key) => {
        const targetIndex = node.outputs[key];
        if (targetIndex != null) {
          visit(targetIndex, depth + 1);
        }
      });
  };

  visit(startIndex, 0);
  return layers;
};

const computeVisitOrder = (startIndex: number, nodeByIndex: Map<number, TopicGraphNodeData>): Map<number, number> => {
  const visitOrder = new Map<number, number>();
  const visited = new Set<number>();
  const queue = [startIndex];
  let order = 0;

  while (queue.length > 0) {
    const index = queue.shift()!;
    if (visited.has(index)) {
      continue;
    }

    visited.add(index);
    visitOrder.set(index, order++);

    const node = nodeByIndex.get(index);
    if (!node) {
      continue;
    }

    Object.keys(node.outputs)
      .sort()
      .forEach((key) => {
        const targetIndex = node.outputs[key];
        if (targetIndex != null && !visited.has(targetIndex)) {
          queue.push(targetIndex);
        }
      });
  }

  return visitOrder;
};

const getParents = (questionIndex: number, questionNodes: TopicGraphNodeData[]): number[] =>
  questionNodes.filter((node) => Object.values(node.outputs).includes(questionIndex)).map((node) => node.questionIndex);

const layoutQuestionPositions = (
  questionNodes: TopicGraphNodeData[],
  { layoutSpacing, verticalPadding }: ReturnType<typeof resolveLayoutOptions>,
): Map<number, [number, number]> => {
  const horizontalGap = NODE_WIDTH + layoutSpacing;
  const positions = new Map<number, [number, number]>();
  if (questionNodes.length === 0) {
    return positions;
  }

  const nodeByIndex = new Map(questionNodes.map((node) => [node.questionIndex, node]));
  const startNode = questionNodes.find((node) => node.input === false);
  if (!startNode) {
    return positions;
  }

  const layers = computeLayers(startNode.questionIndex, nodeByIndex);
  const visitOrder = computeVisitOrder(startNode.questionIndex, nodeByIndex);

  const nodesByLayer = new Map<number, number[]>();
  questionNodes.forEach((node) => {
    const layer = layers.get(node.questionIndex) ?? 0;
    const layerNodes = nodesByLayer.get(layer) ?? [];
    layerNodes.push(node.questionIndex);
    nodesByLayer.set(layer, layerNodes);
  });

  const sortedLayers = Array.from(nodesByLayer.keys()).sort((a, b) => a - b);

  sortedLayers.forEach((layer) => {
    const layerNodeIndexes = (nodesByLayer.get(layer) ?? []).sort((a, b) => {
      const parentsA = getParents(a, questionNodes);
      const parentsB = getParents(b, questionNodes);

      if (parentsA.length > 0 && parentsB.length > 0) {
        const avgParentYA =
          parentsA.reduce((sum, parentIndex) => sum + (positions.get(parentIndex)?.[1] ?? 0), 0) / parentsA.length;
        const avgParentYB =
          parentsB.reduce((sum, parentIndex) => sum + (positions.get(parentIndex)?.[1] ?? 0), 0) / parentsB.length;
        if (avgParentYA !== avgParentYB) {
          return avgParentYA - avgParentYB;
        }
      }

      return (visitOrder.get(a) ?? 0) - (visitOrder.get(b) ?? 0);
    });

    let y = 0;
    layerNodeIndexes.forEach((questionIndex) => {
      const node = nodeByIndex.get(questionIndex);
      if (!node) {
        return;
      }

      positions.set(questionIndex, [layer * horizontalGap, y]);
      y += estimateNodeHeight(node) + verticalPadding;
    });
  });

  return positions;
};

const hasValidPosition = (position: TopicGraphNodeData['position']): position is [number, number] =>
  Array.isArray(position) &&
  position.length >= 2 &&
  typeof position[0] === 'number' &&
  typeof position[1] === 'number' &&
  Number.isFinite(position[0]) &&
  Number.isFinite(position[1]);

const rectanglesOverlap = (
  ax: number,
  ay: number,
  aHeight: number,
  bx: number,
  by: number,
  bHeight: number,
  verticalPadding: number,
): boolean => {
  const horizontalOverlap = Math.abs(ax - bx) < NODE_WIDTH;
  const verticalOverlap = Math.abs(ay - by) < Math.max(aHeight, bHeight) + verticalPadding;
  return horizontalOverlap && verticalOverlap;
};

const questionNodesOverlap = (questionNodes: TopicGraphNodeData[], verticalPadding: number): boolean => {
  for (let i = 0; i < questionNodes.length; i++) {
    const nodeA = questionNodes[i];
    if (!hasValidPosition(nodeA.position)) {
      return true;
    }

    const heightA = estimateNodeHeight(nodeA);
    for (let j = i + 1; j < questionNodes.length; j++) {
      const nodeB = questionNodes[j];
      if (!hasValidPosition(nodeB.position)) {
        return true;
      }

      if (
        rectanglesOverlap(
          nodeA.position[0],
          nodeA.position[1],
          heightA,
          nodeB.position[0],
          nodeB.position[1],
          estimateNodeHeight(nodeB),
          verticalPadding,
        )
      ) {
        return true;
      }
    }
  }

  return false;
};

const applyQuestionPositions = (
  nodes: TopicGraphNodeData[],
  positions: Map<number, [number, number]>,
): TopicGraphNodeData[] =>
  nodes.map((node) => {
    if (node.type === TOPIC_GRAPH_COMMENT_TYPE) {
      return node;
    }

    const position = positions.get(node.questionIndex);
    return position ? { ...node, position } : node;
  });

/**
 * Assigns non-overlapping canvas positions to topic graph question nodes.
 * Mirrors Ariel editor auto-arrange spacing so AI patches render without stacked bubbles.
 */
export const layoutTopicGraph = (
  nodes: TopicGraphNodeData[],
  options?: LayoutTopicGraphOptions,
): TopicGraphNodeData[] => {
  const layoutOptions = resolveLayoutOptions(options);
  const questionNodes = getQuestionNodes(nodes);
  if (questionNodes.length === 0) {
    return nodes;
  }

  if (!questionNodesOverlap(questionNodes, layoutOptions.verticalPadding)) {
    return nodes;
  }

  const positions = layoutQuestionPositions(questionNodes, layoutOptions);
  return applyQuestionPositions(nodes, positions);
};
